import { useCallback } from 'react';
import {
  getStructureMaxBundles,
} from '../../components/product/ProductStructureSelector/structureSelectionState';
import ProductAvailabilityType from '../../types/ProductAvailabilityType';
import ProductClass from '../../types/ProductClass';

export const DEFAULT_MAX_QUANTITY = 99999;

const getPositiveNumber = (value, fallback) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0
    ? numberValue
    : fallback;
};

const getProductFreeQuantity = (product) => {
  const freeQuantity = Number(product && product.free_quantity);
  return Number.isFinite(freeQuantity) ? freeQuantity : DEFAULT_MAX_QUANTITY;
};

const productSellsInPackage = (product) => {
  return (
    product &&
    (product.sellInPackage ||
      (product.package_size &&
        Number(product.package_size) > 0 &&
        Number(product.package_size) !== 1))
  );
};

const getMaxByPackage = (product) => {
  const freeQuantity = getProductFreeQuantity(product);
  const packageSize = getPositiveNumber(product && product.package_size, 1);

  return productSellsInPackage(product)
    ? Math.floor(freeQuantity / packageSize) * packageSize
    : freeQuantity;
};

export const getProductMaxQuantity = ({
  accountStore,
  configStore,
  product,
}) => {
  if (!product) {
    return DEFAULT_MAX_QUANTITY;
  }

  const backOrderLimit =
    configStore.product.backorderLimit > 0
      ? configStore.product.backorderLimit
      : DEFAULT_MAX_QUANTITY;

  const canBeOrderedOutOfStock =
    product.canBeOrderedOutOfStock || product.can_be_ordered_out_of_stock;
  const normalMaxLimit = canBeOrderedOutOfStock
    ? DEFAULT_MAX_QUANTITY
    : getMaxByPackage(product);

  const maxAccountQuantity =
    accountStore.account && accountStore.account.max_product_quantity > 0
      ? accountStore.account.max_product_quantity
      : DEFAULT_MAX_QUANTITY;
  const maxBackorderQuantity =
    product.availability_type === ProductAvailabilityType.ALLOW_BACKORDER
      ? backOrderLimit
      : DEFAULT_MAX_QUANTITY;
  const maxNormalProductQuantity =
    product.availability_type !== ProductAvailabilityType.ALLOW_BACKORDER
      ? normalMaxLimit
      : DEFAULT_MAX_QUANTITY;

  return Math.min(
    maxAccountQuantity,
    maxBackorderQuantity,
    maxNormalProductQuantity,
    DEFAULT_MAX_QUANTITY
  );
};

export const getStructureMaxQuantity = ({
  structureProduct,
  structureProductIds = {},
  cartProductQuantities = {},
}) => {
  if (
    !structureProduct ||
    structureProduct.class !== ProductClass.STRUCTURE ||
    !structureProduct.structure ||
    !Array.isArray(structureProduct.structure.products)
  ) {
    return DEFAULT_MAX_QUANTITY;
  }

  return getStructureMaxBundles({
    product: structureProduct,
    mapping: structureProductIds,
    cartProductQuantities,
  });
};

/**
 *
 * @param {Object} props
 * @param {AccountStore} props.accountStore
 * @param {ConfigStore} props.configStore
 * @param {Product} props.addToCartProduct
 * @param {FullProduct} props.structureProduct
 * @param {Object} props.structureProductIds
 * @param {Object} props.cartProductQuantities
 * @returns {Array.<Function>}
 */
const useMaxQuantity = ({
  accountStore,
  configStore,
  addToCartProduct,
  structureProduct,
  structureProductIds,
  cartProductQuantities,
}) => {
  const getMaxQuantity = useCallback(() => {
    return Math.min(
      getProductMaxQuantity({
        accountStore,
        configStore,
        product: addToCartProduct,
      }),
      getStructureMaxQuantity({
        accountStore,
        configStore,
        structureProduct,
        structureProductIds,
        cartProductQuantities,
      })
    );
  }, [
    accountStore,
    configStore,
    addToCartProduct,
    structureProduct,
    structureProductIds,
    cartProductQuantities,
  ]);

  return [getMaxQuantity];
};

export default useMaxQuantity;
